import { z } from 'zod'
import {
  accountCode,
  categoryEnum,
  docId,
  fundEnum,
  isoTimestamp,
  positiveMoney,
} from './common.js'

/**
 * `budgetCodes` — REQUIREMENTS.md §5.2.
 *
 * Two kinds of document coexist here:
 *
 *   Owned    (`external: false`) — the user's own budget lines. Tracked
 *            balances, dashboard cards, threshold alerts.
 *   External (`external: true`)  — a colleague's code the user occasionally
 *            charges on their behalf. Appears on reconciliation packets;
 *            excluded from every budget calculation.
 *
 * Every budget-math query must filter on `external: false`. Reconciliation
 * packet queries must not (§5.3).
 */

/** Aliases are matched case-insensitively, so they're stored folded. */
export const alias = z
  .string()
  .trim()
  .min(1, 'Alias cannot be empty.')
  .max(32, 'Aliases should be short enough to type quickly.')
  .regex(
    /^[a-z0-9][a-z0-9-]*$/i,
    'Aliases may contain letters, numbers, and hyphens.',
  )
  .transform((s) => s.toLowerCase())

export const budgetCodeSchema = z
  .object({
    id: docId.optional(),
    userId: z.string().min(1),
    fiscalYear: z.number().int().min(2000).max(2100),

    code: accountCode,
    displayName: z.string().trim().min(1).max(120),
    shortLabel: z
      .string()
      .trim()
      .min(1)
      .max(24, 'Short labels are shown where space is tight — keep it to 24 characters or fewer.'),
    fund: fundEnum,
    aliases: z.array(alias).max(10).default([]),
    category: categoryEnum,

    external: z.boolean().default(false),
    externalOwnerName: z.string().trim().max(120).nullable().default(null),

    /** Ignored when `external: true`. */
    beginningBalance: positiveMoney.default(0),
    /** Ignored when `external: true`. Warn above this share of the balance. */
    alertThresholdPercent: z.number().int().min(1).max(100).default(80),

    active: z.boolean().default(true),
    createdAt: isoTimestamp,
    updatedAt: isoTimestamp,
  })
  .refine((c) => !c.external || (c.externalOwnerName?.trim().length ?? 0) > 0, {
    message: 'External codes need an owner name — it drives the grouping in the external charges view.',
    path: ['externalOwnerName'],
  })

export type BudgetCode = z.infer<typeof budgetCodeSchema>

/** Shape accepted from the "Add owned code" form (workflow 7.9). */
export const newOwnedBudgetCodeSchema = z.object({
  code: accountCode,
  displayName: z.string().trim().min(1).max(120),
  shortLabel: z.string().trim().min(1).max(24),
  fund: fundEnum,
  category: categoryEnum,
  aliases: z.array(alias).max(10).default([]),
  beginningBalance: positiveMoney,
  alertThresholdPercent: z.number().int().min(1).max(100).default(80),
})
export type NewOwnedBudgetCode = z.infer<typeof newOwnedBudgetCodeSchema>

/**
 * Shape accepted from the "Add external code" form (workflow 7.7).
 * Deliberately minimal — this is filled in mid-task while filing someone
 * else's receipt, so only the code and owner name are required.
 */
export const newExternalBudgetCodeSchema = z.object({
  code: accountCode,
  externalOwnerName: z.string().trim().min(1).max(120),
  shortLabel: z.string().trim().max(24).optional(),
  aliases: z.array(alias).max(10).default([]),
})
export type NewExternalBudgetCode = z.infer<typeof newExternalBudgetCodeSchema>
